import { map, takeUntil } from 'rxjs/operators';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  PLATFORM_ID,
  Inject,
  ViewChild,
  ViewChildren,
  AfterContentChecked,
  QueryList,
  OnDestroy
} from '@angular/core';
import { IAppState } from '@app/store';
import { Store } from '@ngrx/store';
import { ContentRepositoryService } from '@app/core/services/content/content-repository.service';
import { INavigationItem, formatToNavigationItem, PageData } from '@app/core/header/components/main-nav/navigation';
import { isPlatformServer } from '@angular/common';
import { SetCatalogPageNumber } from '@app/+catalog/store';
import { UnderlineDirective } from '@app/core/header/directives/underline/underline.directive';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'vc-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss']
})
export class MainNavComponent implements OnInit, AfterContentChecked, OnDestroy {
  @Input()
  directShippingEligible: boolean;
  @Output()
  closeMenuMobileEvent: EventEmitter<null> = new EventEmitter();
  @ViewChild(UnderlineDirective, { static: false })
  _underline: UnderlineDirective;
  @ViewChildren('ref') _currentRef: QueryList<any>;
  private _selectedIndex = 0;
  private _selectedIndexChanged = false;
  private ngUnsubscribe = new Subject<void>();

  deviceIsLgUp: boolean;
  timerMenuLevel2: number;
  mainNav: INavigationItem[];
  jsonNav: JSON;
  pageData: PageData = {};
  eventMnemonics = ['black-firday', 'winterdeals', 'summer-sales'];
  isServer = false;
  radioCheckedLevel1: HTMLInputElement;
  radioCheckedLevel2: HTMLInputElement;

  constructor(
    private store: Store<IAppState>,
    private contentRepository: ContentRepositoryService,
    public helperService: HelpersService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  ngOnInit() {
    this.initDevice();
    this.initRouting();
    this.setNavigation();
  }

  private initDevice(): void {
    this.deviceIsLgUp = this.helperService.device.isLgUp();
  }

  private initRouting(): void {
    this.store
      .select('router')
      .pipe(
        map(resp => resp.state),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        resp => {
          if (resp && resp.queryParams && resp.queryParams.q) {
            this.closeMenu();
          }
        },
        err => {
          // Silently catch exception
        }
      );
  }

  checkInput(event): void {
    this.helperService.checkInput(event, this);
  }

  resetMenuStateFromLevel(level) {
    if (!level) {
      return;
    }
    const newList = level.children ? level.children : level;
    if (newList && newList.length > 0) {
      for (let i = 0; i < newList.length; i++) {
        if (newList[i]) {
          newList[i].active = false;
          level = newList;
          this.resetMenuStateFromLevel(level[i]);
        }
      }
    }
  }

  closeMenu() {
    if (this.deviceIsLgUp) {
      const levelToClose = this.mainNav;
      this.resetMenuStateFromLevel(levelToClose);
    } else {
      this.closeMobileMenu();
    }
  }

  closeMobileMenu() {
    this.closeMenuMobileEvent.emit();
  }

  scrollMenuToSeeSubcategories(elem) {
    const linkPos = elem.pageY;
    let newScrollPos;
    const el__mobileMenu = document.getElementsByClassName('secondLine__innerWrapper')[0];
    const mobileNavScrollTop = el__mobileMenu.scrollTop;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    if (linkPos > (viewportHeight * 2) / 3) {
      newScrollPos = mobileNavScrollTop + 100;
      this.scrollTo(el__mobileMenu, newScrollPos, 400);
    }
  }

  scrollTo(element, to, duration) {
    // https://gist.github.com/andjosh/6764939
    const start = element.scrollTop;
    const change = to - start;
    let currentTime = 0;
    const increment = 20;

    function easeInOutQuad(t, b, c, d) {
      t /= d / 2;
      if (t < 1) {
        return (c / 2) * t * t + b;
      }
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    }

    function animateScroll() {
      currentTime += increment;
      const val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollTop = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    }
    animateScroll();
  }

  public resetCatalogPage() {
    this.store.dispatch(new SetCatalogPageNumber({ pageNumber: 0 }));
  }

  public subscribePageParams(): void {
    this.store
      .select('header')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        res => {
          this.pageData = res['pageParams'];
          this.buildNavigation();
        },
        () => {}
      );
  }

  /**
   * Init navigation
   */
  public setNavigation() {
    this.contentRepository
      .getHeader()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        json => {
          this.jsonNav = json;
          this.subscribePageParams();
        },
        () => {}
      );
  }

  private buildNavigation(): void {
    this.mainNav = Object.keys(this.jsonNav).map(itemJson =>
      formatToNavigationItem(this.jsonNav[itemJson], this.pageData)
    );
  }

  /**
   * Underline Menu
   */
  public mouseoverUnderline(i: number) {
    if (this.deviceIsLgUp) {
      this._selectedIndexChanged = this._selectedIndex !== i;
      this._selectedIndex = i;
      this.alignUnderlineToSelectedTab();
    }
  }

  public mouseoutUnderline() {
    this._underline.hide();
  }

  alignUnderlineToSelectedTab(): void {
    const selectedLi =
      this._currentRef && this._currentRef.length
        ? this._currentRef.toArray()[this._selectedIndex].nativeElement
        : null;
    if (selectedLi) {
      this._underline.alignToElement(selectedLi);
    }
  }

  public applyActiveState(elem, state = true) {
    if (this.deviceIsLgUp && elem.children?.length > 0) {
      elem.active = state;
    }
  }

  ngAfterContentChecked(): void {
    // If the selected index has changed, scroll to the label and check if the scrolling controls
    // should be disabled.
    if (this._selectedIndexChanged) {
      this._selectedIndexChanged = false;
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public setLanguageAttribute(label: string) {
    const NAV_LABELS = ['We Love', 'Vintage', 'Lifestyle', 'Best of US', "Asia's Best"];

    if (NAV_LABELS.includes(label)) {
      return 'en';
    }
    return '';
  }
}
